import { useQuery } from "@tanstack/vue-query";
import type { components } from "@scalty/contracts";

type Project = components["schemas"]["Project"];
type ProjectList = components["schemas"]["ProjectList"];
type Me = components["schemas"]["Me"];
type Membership = components["schemas"]["Membership"];

export const PROJECT_SELECTION_STORAGE_KEY = "scalty.current-project-id";

export interface OrganizationMembershipOption {
  membership: Membership;
  active: boolean;
}

interface ActiveOrganizationSwitchDependencies {
  request: (path: string, options: RequestInit) => Promise<unknown>;
  storage: Pick<Storage, "removeItem">;
  fullNavigate: (destination: string) => void;
}

/** Membership choices are derived only from the authenticated /me response. */
export function organizationMembershipOptions(me: Me | null): OrganizationMembershipOption[] {
  return (me?.memberships ?? []).map((membership) => ({
    membership,
    active: membership.organization.id === me?.activeOrganizationId,
  }));
}

/** Resolve an exact server-returned membership and never accept an arbitrary organization id. */
export function switchableOrganizationMembership(
  me: Me | null,
  organizationId: string,
): Membership | null {
  if ((me?.memberships.length ?? 0) <= 1 || organizationId === me?.activeOrganizationId) {
    return null;
  }
  return me?.memberships.find((membership) => membership.organization.id === organizationId) ?? null;
}

/**
 * Change server-side tenant scope before discarding the previous tenant's
 * project choice. A refused request therefore cannot clear storage or navigate.
 */
export async function performActiveOrganizationSwitch(
  organizationId: string,
  dependencies: ActiveOrganizationSwitchDependencies,
): Promise<void> {
  await dependencies.request("/me/active-organization", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ organizationId }),
  });
  dependencies.storage.removeItem(PROJECT_SELECTION_STORAGE_KEY);
  dependencies.fullNavigate("/data-objects");
}

/**
 * Which project the portal works in.
 *
 * A project the customer opened in this visit wins, archived or not: opening an archived project
 * from "Manage projects" has to land in it. The project stored from an earlier visit counts only
 * while it is not archived — a customer who signs in again after archiving it was shown that
 * project and a wizard offering to start work in it (W9-UI-01 dev walk). Otherwise the most
 * recently changed project that is not archived; with none, no project at all, so the portal
 * offers to create one instead of reopening what the customer put away.
 */
export function resolveCurrentProject(
  projects: readonly Project[],
  storedId: string | null,
  openedId: string | null,
): string | null {
  if (openedId !== null && projects.some((p) => p.id === openedId)) return openedId;
  const live = projects.filter((p) => !p.archivedAt);
  if (storedId !== null && live.some((p) => p.id === storedId)) return storedId;
  const latest = [...live].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  return latest?.id ?? null;
}

function createProjectContext() {
  const session = useSession();
  const organizationId = computed(() => session.value.data?.session.activeOrganizationId ?? null);
  // Hydrated once per app from localStorage (client-only; SSR has no storage).
  const selectedId = useState<string | null>("current-project-id", () =>
    import.meta.client ? localStorage.getItem(PROJECT_SELECTION_STORAGE_KEY) : null,
  );
  // The project the customer opened in this visit. Only this choice may land on an archived
  // project; a stored one does not survive archiving (see resolveCurrentProject).
  const openedId = useState<string | null>("current-project-opened", () => null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: computed(() => [...qk.projects(), { organization: organizationId.value }]),
    enabled: computed(() => organizationId.value !== null),
    // Archived projects are in the list on purpose. The list decides which selection is
    // valid, so an active-only list silently turned "open this archived project" into
    // "work in whichever project happens to be first" (W8-WALK). Archiving hides a project
    // from the navigation, which the navigation itself does; it does not make a project the
    // customer explicitly opened cease to exist.
    queryFn: () => apiFetch<ProjectList>("/projects?includeArchived=true"),
    retry: false,
  });

  const projects = computed<Project[]>(() => data.value?.projects ?? []);

  // True once the project list has answered (or failed), or when there is no organisation to
  // ask for one. Project-scoped views wait for this so a first visit never renders "No project"
  // and empty tables for the moment before the list arrives.
  const settled = computed<boolean>(
    () => organizationId.value === null || data.value !== undefined || error.value !== null,
  );

  const currentProjectId = computed<string | null>(() =>
    resolveCurrentProject(projects.value, selectedId.value, openedId.value),
  );

  const currentProject = computed<Project | null>(
    () => projects.value.find((p) => p.id === currentProjectId.value) ?? null,
  );

  function remember(id: string): void {
    selectedId.value = id;
    if (import.meta.client) {
      localStorage.setItem(PROJECT_SELECTION_STORAGE_KEY, id);
    }
  }

  function switchProject(id: string): void {
    openedId.value = id;
    remember(id);
  }

  // When reconciliation lands on a different project than the persisted one
  // (first visit, or the stored project no longer exists), persist the
  // effective id so the choice is stable across reloads.
  if (import.meta.client) {
    watch(
      currentProjectId,
      (id) => {
        if (id !== null && id !== selectedId.value) {
          remember(id);
        }
      },
      { immediate: true },
    );
  }

  return {
    projects,
    currentProject,
    currentProjectId,
    switchProject,
    isPending,
    settled,
    error,
    refetch,
  };
}

// On the client the context lives once for the whole app, held in a detached
// effect scope so its query subscription and persistence watcher survive the
// unmounting of whichever component happened to create it.
let clientContext: ReturnType<typeof createProjectContext> | null = null;

/**
 * Shared project context: the org's project list plus the currently selected
 * project. The selection persists in localStorage across reloads and is
 * reconciled against the loaded list — a stale, missing or archived stored
 * selection falls back to the latest project that is not archived, or null.
 */
export function useProjectContext(): ReturnType<typeof createProjectContext> {
  if (import.meta.server) {
    return createProjectContext();
  }
  if (clientContext === null) {
    // Created lazily inside the first consumer's setup call so that
    // injection-based helpers (query client, Nuxt state) resolve, then kept
    // alive in a scope that outlives that component.
    const context = effectScope(true).run(createProjectContext);
    if (context === undefined) {
      throw new Error("Project context initialization failed");
    }
    clientContext = context;
  }
  return clientContext;
}
